/**
 * Producer Billing Page - "Abonnemang"
 * 
 * Shows current plan and usage vs limits.
 * Uses canonical usage read model via useProducerUsage (RPC-backed).
 * 
 * NOTE: Credits and feature_overrides exist in DB but are dormant (not shown in UI).
 */

import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  CreditCard, 
  AlertTriangle, 
  Clock,
  Shield,
  Info,
  Package,
  Calendar,
  Users
} from 'lucide-react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { 
  useProducerBillingState, 
  useAdminSetProducerPlan,
  useAdminSetPaidPlansEnabled,
  PLAN_LABELS,
} from '@/hooks/useProducerBilling';
import { useProducerUsage } from '@/hooks/useProducerUsage';
import { UsageBadge } from '@/components/producer/UsageBadge';
import { SoftLimitMessage } from '@/components/producer/SoftLimitMessage';

import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { useProducer } from '@/hooks/useProducer';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useState } from 'react';

// Admin section for setting plan directly
function AdminPlanSection({ 
  producerId, 
  currentPlan,
  paidPlansEnabled,
  onPlanUpdated
}: { 
  producerId: string;
  currentPlan: string;
  paidPlansEnabled: boolean;
  onPlanUpdated: () => void;
}) {
  const [selectedPlan, setSelectedPlan] = useState<'free' | 'starter' | 'pro'>(
    currentPlan as 'free' | 'starter' | 'pro'
  );
  const setPlanMutation = useAdminSetProducerPlan();
  const setPaidEnabledMutation = useAdminSetPaidPlansEnabled();
  
  const handleSavePlan = async () => {
    try {
      await setPlanMutation.mutateAsync({ producerId, planId: selectedPlan });
      toast.success('Plan uppdaterad');
      onPlanUpdated();
    } catch (err) {
      toast.error('Kunde inte uppdatera plan');
    }
  };
  
  const handleTogglePaidPlans = async (enabled: boolean) => {
    try {
      await setPaidEnabledMutation.mutateAsync(enabled);
      toast.success(enabled ? 'Betalplaner aktiverade' : 'Betalplaner avstängda');
      onPlanUpdated();
    } catch (err) {
      toast.error('Kunde inte ändra betalplan-status');
    }
  };
  
  return (
    <Card className="border-amber-500/50 bg-amber-50/30 dark:bg-amber-950/10">
      <CardHeader className="flex flex-row items-center gap-4">
        <div className="rounded-lg bg-amber-500/10 p-2">
          <Shield className="h-6 w-6 text-amber-600" />
        </div>
        <div className="flex-1">
          <CardTitle className="text-lg">Admin-kontroll</CardTitle>
          <CardDescription>Sätt plan direkt utan betalning</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Global toggle */}
        <div className="flex items-center justify-between">
          <Label htmlFor="paid-toggle" className="flex flex-col gap-1">
            <span>Aktivera betalda planer</span>
            <span className="text-xs text-muted-foreground font-normal">
              Om av: alla värdar får Gratis-planen
            </span>
          </Label>
          <Switch
            id="paid-toggle"
            checked={paidPlansEnabled}
            onCheckedChange={handleTogglePaidPlans}
            disabled={setPaidEnabledMutation.isPending}
          />
        </div>
        
        <div className="border-t pt-4" />
        
        {/* Plan selector */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Label htmlFor="plan-select" className="mb-1.5 block text-sm">
              Sätt plan för denna värd
            </Label>
            <Select 
              value={selectedPlan} 
              onValueChange={(v) => setSelectedPlan(v as 'free' | 'starter' | 'pro')}
            >
              <SelectTrigger id="plan-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Gratis</SelectItem>
                <SelectItem value="starter">Starter</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={handleSavePlan}
            disabled={setPlanMutation.isPending || selectedPlan === currentPlan}
            className="sm:self-end"
          >
            {setPlanMutation.isPending ? 'Sparar...' : 'Spara plan'}
          </Button>
        </div>
        
        <p className="text-xs text-muted-foreground">
          Nuvarande effective_plan: <Badge variant="outline">{currentPlan}</Badge>
          {' '}| paid_plans_enabled: <Badge variant={paidPlansEnabled ? 'default' : 'secondary'}>
            {paidPlansEnabled ? 'JA' : 'NEJ'}
          </Badge>
        </p>
      </CardContent>
    </Card>
  );
}

// ── Plan usage display ──
function PlanUsageSection({ producerId }: { producerId: string }) {
  const { data: usage, isLoading } = useProducerUsage(producerId);

  if (isLoading) return <Skeleton className="h-24 w-full" />;
  if (!usage) return null;

  const items = [
    { label: 'Aktiviteter', icon: Calendar, used: usage.events_used, limit: usage.events_limit, noun: 'aktiva aktiviteter' },
    { label: 'Produkter', icon: Package, used: usage.products_used, limit: usage.products_limit, noun: 'produkter' },
    { label: 'Boenden', icon: Package, used: usage.accommodations_used, limit: usage.accommodations_limit, noun: 'boenden' },
    { label: 'Loppis', icon: Package, used: usage.flea_markets_used, limit: usage.flea_markets_limit, noun: 'loppis' },
    { label: 'Värdteam', icon: Users, used: usage.team_members_used, limit: usage.team_members_limit, noun: 'teammedlemmar' },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Användning just nu</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3">
          {items.map((item) => {
            const usageText = item.limit === null
              ? `${item.used} av obegränsat ${item.noun}`
              : `${item.used} av ${item.limit} ${item.noun}`;
            const isNear = item.limit !== null && item.used >= item.limit * 0.8;
            const isAtLimit = item.limit !== null && item.used >= item.limit;
            return (
              <div
                key={item.label}
                className={cn(
                  'flex items-center gap-3 rounded-lg border p-3',
                  isAtLimit
                    ? 'border-amber-300 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20'
                    : isNear
                      ? 'border-amber-200/60 bg-amber-50/30 dark:border-amber-900/40 dark:bg-amber-950/10'
                      : ''
                )}
              >
                <item.icon className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{item.label}</p>
                  <p className={cn('text-sm text-muted-foreground')}>
                    {usageText}
                  </p>
                </div>
                <UsageBadge used={item.used} limit={item.limit} label={item.label} />
              </div>
            );
          })}
        </div>

        <SoftLimitMessage
          used={usage.events_used}
          limit={usage.events_limit}
          resourceName="aktiva aktiviteter"
        />
      </CardContent>
    </Card>
  );
}

export default function ProducerBillingPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const { isAdmin } = useAuth();
  
  const { producer } = useProducer(farmId || '');
  const { data: billing, isLoading, error, refetch } = useProducerBillingState(farmId);
  
  if (isLoading) {
    return (
      <ProducerLayout farmId={farmId || ''} producerName={producer?.name} title="Abonnemang">
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </ProducerLayout>
    );
  }
  
  if (error || !billing) {
    return (
      <ProducerLayout farmId={farmId || ''} producerName={producer?.name} title="Abonnemang">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Kunde inte ladda abonnemangsinformation</AlertTitle>
          <AlertDescription>
            {error?.message || 'Ett oväntat fel uppstod'}
          </AlertDescription>
        </Alert>
      </ProducerLayout>
    );
  }
  
  const planInfo = PLAN_LABELS[billing.effective_plan] || { name: 'Gratis', description: '' };
  
  return (
    <ProducerLayout farmId={farmId || ''} producerName={producer?.name} title="Abonnemang">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Abonnemang</h1>
          <p className="text-muted-foreground">
            Se din aktuella plan och hur mycket du använder just nu.
          </p>
        </div>
        
        {/* Admin section - only visible to admins */}
        {isAdmin && farmId && (
          <AdminPlanSection
            producerId={farmId}
            currentPlan={billing.effective_plan}
            paidPlansEnabled={billing.paid_plans_enabled}
            onPlanUpdated={() => refetch()}
          />
        )}
        
        {/* Global kill-switch warning - show when paid plans are disabled */}
        {!billing.paid_plans_enabled && (
          <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
            <Info className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-800 dark:text-amber-200">
              Betalplaner är tillfälligt avstängda
            </AlertTitle>
            <AlertDescription className="text-amber-700 dark:text-amber-300">
              Du använder just nu Gratis-planen. Starter och Pro öppnas senare.
            </AlertDescription>
          </Alert>
        )}
        
        {/* Override notice */}
        {billing.override && billing.paid_plans_enabled && (
          <Alert>
            <Clock className="h-4 w-4" />
            <AlertTitle>Plan override aktiv</AlertTitle>
            <AlertDescription>
              Din gård körs med en tillfällig plan-override ({PLAN_LABELS[billing.override.plan]?.name}).
              {billing.override.expires_at && (
                <> Utgår: {format(new Date(billing.override.expires_at), 'PPP', { locale: sv })}</>
              )}
              {billing.override.note && <> — {billing.override.note}</>}
            </AlertDescription>
          </Alert>
        )}
        
        {/* Din plan */}
        <Card>
          <CardHeader className="flex flex-row items-center gap-4">
            <div className="rounded-lg bg-primary/10 p-2">
              <CreditCard className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg">Din plan</CardTitle>
              <CardDescription>Aktuell plan för gården</CardDescription>
            </div>
            <Badge variant={billing.effective_plan === 'free' ? 'outline' : 'default'}>
              {planInfo.name}
            </Badge>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{planInfo.name}</p>
            <p className="text-sm text-muted-foreground">{planInfo.description}</p>
            {!billing.paid_plans_enabled && billing.subscription?.plan_id !== 'free' && (
              <p className="text-xs text-amber-600 mt-2">
                Din faktiska prenumeration är {PLAN_LABELS[billing.subscription?.plan_id || 'free']?.name}, 
                men betalplaner är tillfälligt avstängda.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Usage from canonical server-side read model */}
        {farmId && <PlanUsageSection producerId={farmId} />}
      </div>
    </ProducerLayout>
  );
}
